import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Shield, 
  Zap, 
  Users, 
  Code, 
  Scale, 
  Stethoscope, 
  Building2, 
  Server,
  BookOpen,
  ChevronRight,
  Menu,
  X,
  Star,
  Check,
  ArrowRight,
  Send,
  Target,
  Crosshair,
  Radio,
  Radar,
  AlertTriangle
} from "lucide-react";
import ParticleBackground, { MatrixBackground } from "./ParticleBackground";
import MatrixRain from "./MatrixRain";
import DecryptionLoader from "./DecryptionLoader";
import Marquee from "react-fast-marquee";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Alpha1Logo = ({ size = "default" }) => {
  const sizeClasses = {
    small: "w-8 h-8",
    default: "w-12 h-12",
    large: "w-20 h-20"
  };

  return (
    <div className={`${sizeClasses[size]} relative`}>
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
          <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00FF41" />
            <stop offset="50%" stopColor="#00FFC8" />
            <stop offset="100%" stopColor="#39FF14" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <polygon 
          points="50,2 95,25 95,75 50,98 5,75 5,25" 
          fill="none" 
          stroke="url(#logoGradient)" 
          strokeWidth="2"
          filter="url(#glow)"
        />
        <circle cx="50" cy="50" r="30" fill="none" stroke="#00FF41" strokeWidth="1" opacity="0.5" />
        <line x1="50" y1="20" x2="50" y2="35" stroke="#00FF41" strokeWidth="2" />
        <line x1="50" y1="65" x2="50" y2="80" stroke="#00FF41" strokeWidth="2" />
        <line x1="20" y1="50" x2="35" y2="50" stroke="#00FF41" strokeWidth="2" />
        <line x1="65" y1="50" x2="80" y2="50" stroke="#00FF41" strokeWidth="2" />
        <circle cx="50" cy="50" r="5" fill="#00FF41" filter="url(#glow)" />
        <text x="50" y="58" textAnchor="middle" fill="#00FF41" fontSize="20" fontFamily="Orbitron" fontWeight="bold">α1</text>
      </svg>
    </div>
  );
};

const Navbar = ({ onLogin }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'glass border-b border-primary/20' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-4"
          >
            <Alpha1Logo size="default" />
            <div className="flex flex-col">
              <span className="font-military text-xl tracking-wider text-primary text-glow-primary">ALPHA1-ZERO</span>
              <span className="text-[10px] font-mono text-primary/60 tracking-[0.3em]">SKIDRIVE TECHNOLOGIES</span>
            </div>
          </motion.div>

          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm font-mono text-primary/70 hover:text-primary transition-colors tracking-wider">FEATURES</a>
            <a href="#use-cases" className="text-sm font-mono text-primary/70 hover:text-primary transition-colors tracking-wider">OPERATIONS</a>
            <a href="#pricing" className="text-sm font-mono text-primary/70 hover:text-primary transition-colors tracking-wider">PRICING</a>
            <a href="#contact" className="text-sm font-mono text-primary/70 hover:text-primary transition-colors tracking-wider">CONTACT</a>
            <button 
              onClick={onLogin}
              data-testid="nav-login-btn"
              className="cyber-btn text-sm"
            >
              <span className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                DEPLOY NOW
              </span>
            </button>
          </div>

          <button 
            className="md:hidden text-primary"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden pb-4 border-t border-primary/20"
            >
              <div className="flex flex-col gap-4 pt-4">
                <a href="#features" className="text-sm font-mono text-primary/70 hover:text-primary">FEATURES</a>
                <a href="#use-cases" className="text-sm font-mono text-primary/70 hover:text-primary">OPERATIONS</a>
                <a href="#pricing" className="text-sm font-mono text-primary/70 hover:text-primary">PRICING</a>
                <a href="#contact" className="text-sm font-mono text-primary/70 hover:text-primary">CONTACT</a>
                <button onClick={onLogin} className="cyber-btn text-sm w-fit" data-testid="mobile-login-btn">
                  DEPLOY NOW
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

const Hero = ({ onLogin }) => (
  <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20" style={{ backgroundColor: '#000500' }}>
    <MatrixRain />
    
    <div 
      className="fixed inset-0 pointer-events-none"
      style={{
        zIndex: 1,
        opacity: 0.1,
        backgroundImage: `
          linear-gradient(rgba(0, 255, 65, 0.15) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0, 255, 65, 0.15) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px'
      }}
    />
    
    <ParticleBackground />
    
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-primary/5 rounded-full blur-[200px] pointer-events-none" />
    
    <div className="absolute top-24 left-8 w-20 h-20 border-l-2 border-t-2 border-primary/50" />
    <div className="absolute top-24 right-8 w-20 h-20 border-r-2 border-t-2 border-primary/50" />
    <div className="absolute bottom-8 left-8 w-20 h-20 border-l-2 border-b-2 border-primary/50" />
    <div className="absolute bottom-8 right-8 w-20 h-20 border-r-2 border-b-2 border-primary/50" />
    
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-3 px-6 py-3 border border-primary/30 bg-black/50 mb-8"
        >
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
          </span>
          <span className="text-xs font-mono text-primary uppercase tracking-[0.3em]">
            SYSTEM ONLINE // TACTICAL AI PLATFORM
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-4"
        >
          <h1 className="font-military text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-none tracking-wider">
            <span className="text-white text-glow-primary">ALPHA1</span>
            <span className="text-primary">-ZERO</span>
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-6"
        >
          <p className="font-orbitron text-xl md:text-2xl text-primary/80 tracking-widest">
            AUTONOMOUS AI WARFARE SYSTEM
          </p>
          <p className="text-sm font-mono text-primary/50 mt-2 tracking-[0.2em]">
            BY SKIDRIVE TECHNOLOGIES
          </p>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10 font-mono"
        >
          Deploy sovereign AI agents with military-grade encryption. 
          Execute autonomous operations within air-gapped infrastructure.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button 
            onClick={onLogin}
            data-testid="hero-get-started-btn"
            className="cyber-btn text-lg px-10 py-4 flex items-center gap-3"
          >
            <Crosshair className="w-5 h-5" />
            INITIALIZE SYSTEM
            <ChevronRight className="w-5 h-5" />
          </button>
          <a 
            href="#use-cases"
            className="flex items-center gap-2 text-primary/70 hover:text-primary transition-colors font-mono text-sm tracking-wider"
          >
            <span>VIEW OPERATIONS</span>
            <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20"
        >
          {[
            { value: "99.9%", label: "UPTIME SLA", icon: Radio },
            { value: "50ms", label: "RESPONSE TIME", icon: Zap },
            { value: "SOC2", label: "CERTIFIED", icon: Shield },
            { value: "∞", label: "SCALABILITY", icon: Radar },
          ].map((stat, i) => (
            <motion.div 
              key={i} 
              className="text-center p-4 border border-primary/20 bg-black/30"
              whileHover={{ borderColor: 'rgba(0, 255, 65, 0.5)', scale: 1.02 }}
            >
              <stat.icon className="w-5 h-5 text-primary mx-auto mb-2" />
              <div className="text-3xl md:text-4xl font-orbitron font-bold text-primary text-glow-primary">
                {stat.value}
              </div>
              <div className="text-[10px] text-primary/50 uppercase tracking-[0.2em] mt-1 font-mono">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);

const TrustedBy = () => (
  <section className="py-12 border-y border-primary/10 bg-black/50">
    <div className="text-center mb-8">
      <span className="text-[10px] font-mono text-primary/40 uppercase tracking-[0.3em]">
        DEPLOYED BY LEADING ORGANIZATIONS
      </span>
    </div>
    <Marquee gradient={false} speed={30} className="py-4">
      {["DEFENSE CONTRACTORS", "CYBER COMMAND", "INTELLIGENCE AGENCIES", "FINANCIAL INSTITUTIONS", "CRITICAL INFRASTRUCTURE", "RESEARCH DIVISIONS"].map((name, i) => (
        <div key={i} className="mx-16 flex items-center gap-3 text-primary/30">
          <Shield className="w-4 h-4" />
          <span className="font-mono text-sm tracking-[0.2em]">{name}</span>
        </div>
      ))}
    </Marquee>
  </section>
);

const Features = () => {
  const features = [
    {
      icon: Target,
      title: "Multi-Agent Architecture",
      description: "Deploy coordinated AI agents that execute complex tactical operations autonomously.",
      code: "AGENT_SWARM_v2.1"
    },
    {
      icon: Shield,
      title: "Sovereign Deployment",
      description: "Air-gapped installations with zero external dependencies. Full operational control.",
      code: "AIRGAP_SECURE"
    },
    {
      icon: Radio,
      title: "Neural Memory Layer",
      description: "Persistent encrypted context across all operations with zero data leakage.",
      code: "MEM_ENCRYPT_256"
    },
    {
      icon: Zap,
      title: "Action-Oriented Ops",
      description: "Agents that execute, not just analyze. Real-time autonomous task completion.",
      code: "EXEC_MODE_ALPHA"
    },
    {
      icon: Users,
      title: "Skill Marketplace",
      description: "Extend agent capabilities with specialized combat-tested skills and workflows.",
      code: "SKILL_REPO_v3"
    },
    {
      icon: Code,
      title: "Full API Access",
      description: "Military-spec APIs and SDKs for seamless integration with existing systems.",
      code: "API_MIL_STD"
    }
  ];

  return (
    <section id="features" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 grid-bg" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[10px] font-mono text-primary uppercase tracking-[0.3em]">
            // CORE CAPABILITIES
          </span>
          <h2 className="font-military text-4xl md:text-5xl mt-4">
            TACTICAL <span className="text-primary text-glow-primary">AI INFRASTRUCTURE</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="cyber-card p-6 group relative overflow-hidden"
              data-testid={`feature-card-${i}`}
            >
              <div className="absolute top-0 right-0 w-16 h-16 border-t border-r border-primary/30" />
              
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 border border-primary/30 flex items-center justify-center bg-primary/5 group-hover:bg-primary/10 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <span className="text-[10px] font-mono text-primary/50 tracking-wider">{feature.code}</span>
                  <h3 className="font-orbitron text-lg font-bold mt-1 group-hover:text-primary transition-colors">
                    {feature.title}
                  </h3>
                </div>
              </div>
              <p className="text-gray-400 text-sm font-mono leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const UseCases = () => {
  const cases = [
    {
      icon: Code,
      title: "Software Development",
      description: "Automated code generation, security analysis, and deployment pipelines.",
      stats: ["10x FASTER", "95% COVERAGE", "ZERO DOWNTIME"],
    },
    {
      icon: BookOpen,
      title: "Intelligence Analysis",
      description: "Real-time data processing, pattern recognition, and threat assessment.",
      stats: ["500+ SOURCES", "REAL-TIME", "AUTO REPORTS"],
    },
    {
      icon: Scale,
      title: "Legal Operations",
      description: "Contract analysis, compliance monitoring, and document processing.",
      stats: ["90% FASTER", "99.5% ACCURACY", "FULL AUDIT"],
    },
    {
      icon: Server,
      title: "Infrastructure Ops",
      description: "Automated deployment, monitoring, and incident response systems.",
      stats: ["24/7 WATCH", "AUTO-HEAL", "COST OPT"],
    },
    {
      icon: Stethoscope,
      title: "Healthcare Systems",
      description: "HIPAA-compliant processing, clinical workflows, and diagnostics.",
      stats: ["HIPAA CERT", "HL7/FHIR", "ENCRYPTED"],
    },
    {
      icon: Building2,
      title: "Government & Defense",
      description: "Classified workloads, air-gapped systems, and sovereign operations.",
      stats: ["FEDRAMP HIGH", "AIRGAPPED", "ZERO TRUST"],
    }
  ];

  return (
    <section id="use-cases" className="py-24 md:py-32 bg-surface relative">
      <div className="absolute inset-0 opacity-50">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(0, 255, 65, 0.03) 0%, transparent 50%)'
        }} />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[10px] font-mono text-secondary uppercase tracking-[0.3em]">
            // OPERATIONAL SECTORS
          </span>
          <h2 className="font-military text-4xl md:text-5xl mt-4">
            MISSION <span className="text-secondary">CRITICAL</span> DEPLOYMENTS
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cases.map((useCase, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="holo-card overflow-hidden group"
              data-testid={`usecase-card-${i}`}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 border border-secondary/30 flex items-center justify-center">
                    <useCase.icon className="w-5 h-5 text-secondary" />
                  </div>
                  <h3 className="font-orbitron font-bold">{useCase.title}</h3>
                </div>
                <p className="text-gray-400 text-sm font-mono mb-4">{useCase.description}</p>
                <div className="flex flex-wrap gap-2">
                  {useCase.stats.map((stat, j) => (
                    <span key={j} className="text-[10px] font-mono px-2 py-1 border border-primary/20 text-primary/70">
                      {stat}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Pricing = ({ onLogin }) => {
  const plans = [
    {
      name: "TACTICAL",
      price: "499",
      description: "For emerging operations",
      features: ["5 AI Agents", "Basic Skills", "Standard Support", "Cloud Deploy", "99.5% SLA"],
      cta: "INITIALIZE",
      tier: "TIER-1"
    },
    {
      name: "STRATEGIC",
      price: "2,499",
      description: "For advanced operations",
      features: ["Unlimited Agents", "Full Arsenal", "Priority Support", "Hybrid Deploy", "99.9% SLA", "Custom Integrations", "Dedicated Ops Manager"],
      cta: "ENGAGE",
      tier: "TIER-2",
      popular: true
    },
    {
      name: "SOVEREIGN",
      price: "CLASSIFIED",
      description: "For classified operations",
      features: ["Air-Gapped Deploy", "On-Premise Install", "FedRAMP High", "Custom Security", "Dedicated Infra", "24/7 Support", "SLA Custom"],
      cta: "REQUEST BRIEF",
      tier: "TIER-3"
    }
  ];

  return (
    <section id="pricing" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 grid-bg opacity-30" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[10px] font-mono text-primary uppercase tracking-[0.3em]">
            // DEPLOYMENT TIERS
          </span>
          <h2 className="font-military text-4xl md:text-5xl mt-4">
            SELECT YOUR <span className="text-primary text-glow-primary">CLEARANCE</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`cyber-card p-8 relative ${plan.popular ? 'border-primary' : ''}`}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
            >
              {plan.popular && (
                <div className="absolute -top-px left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />
              )}
              
              <div className="text-center mb-6">
                <span className="text-[10px] font-mono text-primary/50 tracking-wider">{plan.tier}</span>
                <h3 className="font-military text-2xl mt-1">{plan.name}</h3>
                <div className="mt-4">
                  <span className="text-4xl font-orbitron font-bold text-primary">
                    {plan.price === "CLASSIFIED" ? "" : "$"}{plan.price}
                  </span>
                  {plan.price !== "CLASSIFIED" && (
                    <span className="text-gray-500 text-sm font-mono">/month</span>
                  )}
                </div>
                <p className="text-gray-500 text-sm mt-2 font-mono">{plan.description}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, j) => (
                  <li key={j} className="flex items-center gap-3 text-sm font-mono text-gray-300">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <button 
                onClick={onLogin}
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                className={`w-full py-3 font-orbitron font-bold uppercase tracking-wider transition-all ${
                  plan.popular 
                    ? 'bg-primary text-black hover:bg-primary-bright' 
                    : 'border border-primary/30 text-primary hover:border-primary hover:bg-primary/10'
                }`}
              >
                {plan.cta}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    request_type: "demo"
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error("Contact submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-surface relative">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-[10px] font-mono text-primary uppercase tracking-[0.3em]">
            // SECURE CHANNEL
          </span>
          <h2 className="font-military text-4xl md:text-5xl mt-4">
            REQUEST <span className="text-primary text-glow-primary">BRIEFING</span>
          </h2>
          <p className="text-gray-400 mt-4 font-mono text-sm">
            Initiate contact for a classified demonstration of Alpha1-Zero capabilities.
          </p>
        </motion.div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="cyber-card p-12 text-center"
            data-testid="contact-success"
          >
            <div className="w-16 h-16 border border-primary flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-orbitron text-2xl font-bold mb-2">TRANSMISSION RECEIVED</h3>
            <p className="text-gray-400 font-mono text-sm">Secure response within 24 hours.</p>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="cyber-card p-8 space-y-6"
            data-testid="contact-form"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-mono text-primary/70 uppercase tracking-wider mb-2">
                  CALLSIGN *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="cyber-input w-full"
                  placeholder="Enter callsign"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono text-primary/70 uppercase tracking-wider mb-2">
                  SECURE CHANNEL *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="cyber-input w-full"
                  placeholder="secure@domain.mil"
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-mono text-primary/70 uppercase tracking-wider mb-2">
                ORGANIZATION
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="cyber-input w-full"
                placeholder="Organization name"
                data-testid="contact-company-input"
              />
            </div>

            <div>
              <label className="block text-[10px] font-mono text-primary/70 uppercase tracking-wider mb-2">
                OPERATION TYPE
              </label>
              <select
                value={formData.request_type}
                onChange={(e) => setFormData({ ...formData, request_type: e.target.value })}
                className="cyber-input w-full"
                data-testid="contact-type-select"
              >
                <option value="demo">TACTICAL DEMO</option>
                <option value="enterprise">ENTERPRISE DEPLOYMENT</option>
                <option value="contact">GENERAL INQUIRY</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-mono text-primary/70 uppercase tracking-wider mb-2">
                MISSION BRIEF *
              </label>
              <textarea
                required
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="cyber-input w-full resize-none"
                placeholder="Describe your operational requirements..."
                data-testid="contact-message-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="cyber-btn w-full flex items-center justify-center gap-2"
              data-testid="contact-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  TRANSMIT
                </>
              )}
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="py-12 border-t border-primary/10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <Alpha1Logo size="small" />
          <div>
            <span className="font-military text-sm tracking-wider text-primary">ALPHA1-ZERO</span>
            <p className="text-[10px] font-mono text-primary/40 tracking-wider">SKIDRIVE TECHNOLOGIES</p>
          </div>
        </div>
        
        <div className="flex items-center gap-8 text-xs font-mono text-primary/40">
          <a href="#" className="hover:text-primary transition-colors tracking-wider">SECURITY</a>
          <a href="#" className="hover:text-primary transition-colors tracking-wider">COMPLIANCE</a>
          <a href="#" className="hover:text-primary transition-colors tracking-wider">STATUS</a>
          <a href="#" className="hover:text-primary transition-colors tracking-wider">DOCS</a>
        </div>

        <div className="text-[10px] font-mono text-primary/30 tracking-wider">
          © 2026 SKIDRIVE TECHNOLOGIES // ALL RIGHTS RESERVED
        </div>
      </div>
    </div>
  </footer>
);

const LandingPage = ({ onLogin }) => {
  const [showLoader, setShowLoader] = useState(() => {
    if (sessionStorage.getItem('alpha1_decrypted')) return false;
    return true;
  });

  const handleLoaderComplete = () => {
    sessionStorage.setItem('alpha1_decrypted', 'true');
    setShowLoader(false);
  };

  return (
    <div className="min-h-screen bg-[#000a00]">
      {showLoader && <DecryptionLoader onComplete={handleLoaderComplete} />}
      <Navbar onLogin={onLogin} />
      <Hero onLogin={onLogin} />
      <TrustedBy />
      <Features />
      <UseCases />
      <Pricing onLogin={onLogin} />
      <Contact />
      <Footer />
    </div>
  );
};

export default LandingPage;
